import { z } from "zod";

const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const timeOnly = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
const notes = z.string().trim().max(2000).nullable().optional();

export const TIME_ENTRY_KINDS = [
  "attendance",
  "vacation",
  "sick",
  "military",
  "accident",
  "compensation_leave",
  "unpaid_leave",
  "other_absence",
] as const;
export type TimeEntryKind = (typeof TIME_ENTRY_KINDS)[number];

export const timeEntryCreateSchema = z
  .object({
    date: dateOnly,
    kind: z.enum(TIME_ENTRY_KINDS),
    startTime: timeOnly.nullable().optional(),
    endTime: timeOnly.nullable().optional(),
    minutes: z
      .number()
      .int()
      .min(1)
      .max(24 * 60)
      .nullable()
      .optional(),
    notes,
  })
  .superRefine((value, ctx) => {
    const hasTimes = Boolean(value.startTime || value.endTime);
    if (hasTimes && (!value.startTime || !value.endTime)) {
      ctx.addIssue({
        code: "custom",
        path: ["endTime"],
        message: "Start and end time are both required",
      });
    }
    if (value.startTime && value.endTime && value.endTime <= value.startTime) {
      ctx.addIssue({
        code: "custom",
        path: ["endTime"],
        message: "End time must be after start time",
      });
    }
    if (!hasTimes && !value.minutes) {
      ctx.addIssue({
        code: "custom",
        path: ["minutes"],
        message: "Duration is required",
      });
    }
  });
export type TimeEntryCreateDto = z.infer<typeof timeEntryCreateSchema>;

export const timeEntryUpdateSchema = timeEntryCreateSchema.and(
  z.object({ version: z.number().int().positive() }),
);
export type TimeEntryUpdateDto = z.infer<typeof timeEntryUpdateSchema>;

export const timeStampSchema = z.object({
  action: z.enum(["clock_in", "clock_out"]),
  at: z.string().datetime().optional(),
  notes,
});
export type TimeStampDto = z.infer<typeof timeStampSchema>;

export const timeAllocationCellSchema = z
  .object({
    date: dateOnly,
    projectId: z.string().uuid(),
    minutes: z
      .number()
      .int()
      .min(0)
      .max(24 * 60),
    sheetVersion: z.number().int().positive().nullable().optional(),
  })
  .strict();
export type TimeAllocationCellDto = z.infer<typeof timeAllocationCellSchema>;

export const timeAssignmentMatrixQuerySchema = z
  .object({
    from: dateOnly,
    to: dateOnly,
  })
  .strict()
  .superRefine((value, ctx) => {
    const from = Date.parse(`${value.from}T00:00:00.000Z`);
    const to = Date.parse(`${value.to}T00:00:00.000Z`);
    if (to < from || to - from > 41 * 86_400_000) {
      ctx.addIssue({
        code: "custom",
        path: ["to"],
        message: "Assignment range must contain between 1 and 42 days",
      });
    }
  });
export type TimeAssignmentMatrixQueryDto = z.infer<
  typeof timeAssignmentMatrixQuerySchema
>;
export const timesheetDecisionSchema = z
  .object({
    version: z.number().int().positive(),
    action: z.enum(["approve", "reject", "reopen"]),
    reason: z.string().trim().max(2000).nullable().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.action === "reject" && !value.reason) {
      ctx.addIssue({
        code: "custom",
        path: ["reason"],
        message: "A rejection reason is required",
      });
    }
  });
export type TimesheetDecisionDto = z.infer<typeof timesheetDecisionSchema>;

/** The employee hands in (or takes back) their own week. */
export const timesheetSubmitSchema = z
  .object({
    version: z.number().int().positive(),
    action: z.enum(["submit", "withdraw"]),
  })
  .strict();
export type TimesheetSubmitDto = z.infer<typeof timesheetSubmitSchema>;

export const HOLIDAY_RECURRENCE_TYPES = [
  "none",
  "yearly_date",
  "month_day",
  "nth_weekday",
] as const;
export type HolidayRecurrenceType = (typeof HOLIDAY_RECURRENCE_TYPES)[number];
export const companyHolidaySchema = z
  .object({
    date: dateOnly,
    name: z.string().trim().min(1).max(160),
    paid: z.boolean().default(true),
    durationMinutes: z
      .number()
      .int()
      .min(1)
      .max(24 * 60)
      .nullable()
      .optional(),
    notes,
    recurrenceType: z.enum(HOLIDAY_RECURRENCE_TYPES).default("none"),
    recurrenceMonth: z.number().int().min(1).max(12).nullable().optional(),
    recurrenceDay: z.number().int().min(1).max(31).nullable().optional(),
    recurrenceWeekday: z.number().int().min(0).max(6).nullable().optional(),
    recurrenceOrdinal: z.number().int().min(1).max(5).nullable().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.recurrenceType === "month_day" && !value.recurrenceDay) {
      ctx.addIssue({
        code: "custom",
        path: ["recurrenceDay"],
        message: "A day of month is required",
      });
    }
    if (value.recurrenceType === "month_day" && !value.recurrenceMonth) {
      ctx.addIssue({
        code: "custom",
        path: ["recurrenceMonth"],
        message: "A month is required",
      });
    }
    if (value.recurrenceType === "nth_weekday") {
      if (!value.recurrenceMonth)
        ctx.addIssue({
          code: "custom",
          path: ["recurrenceMonth"],
          message: "A month is required",
        });
      if (
        value.recurrenceWeekday === null ||
        value.recurrenceWeekday === undefined
      )
        ctx.addIssue({
          code: "custom",
          path: ["recurrenceWeekday"],
          message: "A weekday is required",
        });
      if (!value.recurrenceOrdinal)
        ctx.addIssue({
          code: "custom",
          path: ["recurrenceOrdinal"],
          message: "An ordinal is required",
        });
    }
  });
export type CompanyHolidayDto = z.infer<typeof companyHolidaySchema>;

export const timeWeekQuerySchema = z.object({
  week: dateOnly.optional(),
  userId: z.string().uuid().optional(),
});

export const timeManagementQuerySchema = z.object({
  week: dateOnly.optional(),
  status: z
    .enum(["all", "draft", "submitted", "approved", "rejected"])
    .default("all"),
  search: z.string().trim().max(120).optional(),
  userId: z.string().uuid().optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
